import Swal from "sweetalert2";

/**
 * Displays a success alert dialog with customizable content and styling.
 * 
 * @param {string | undefined} body - The main message content of the alert
 * @param {string | undefined} [title] - The title of the alert
 * @param {boolean | undefined} [icon=true] - Whether to show the success icon
 * @param {boolean | undefined} [isHtml=false] - Whether the body content is HTML
 * 
 * @example
 * ```tsx
 * await showSuccessAlert(
 *   "Operation completed successfully",
 *   "Success",
 *   true,
 *   false
 * );
 * ```
 * 
 * @returns {Promise<void>} A promise that resolves when the alert is closed
 */
export const showSuccessAlert = async (
  body: string | undefined,
  title?: string | undefined,
  icon: boolean | undefined = true,
  isHtml: boolean | undefined = false
) => {
  const mode = localStorage.getItem("joy-mode");
  const customClass = {
    popup: "swal2-popup-dark ",
    title: "swal2-title-dark",
    confirmButton: "swal2-confirm-dark",
    cancelButton: "swal2-cancel-dark",
  };
  try {
    await Swal.fire({
      icon: icon ? "success" : undefined,
      title: title,
      confirmButtonColor: "#a5dc86",
      cancelButtonColor: "#d33",
      confirmButtonText: "Ok",
      ...(isHtml ? { html: body } : { text: body }),
      customClass: mode === "dark" ? { ...customClass } : {},
    });
  } catch (err) {
    console.error("An error occurred while showing success alert:", err);
  }
};

/**
 * Displays an error alert dialog with customizable content and styling.
 * 
 * @param {string | undefined} body - The main message content of the alert
 * @param {string | undefined} [title] - The title of the alert
 * @param {boolean | undefined} [icon=true] - Whether to show the error icon
 * @param {boolean | undefined} [isHtml=false] - Whether the body content is HTML
 * 
 * @example
 * ```tsx
 * await showErrorAlert(
 *   "An error occurred during the operation",
 *   "Error",
 *   true,
 *   false
 * );
 * ```
 * 
 * @returns {Promise<void>} A promise that resolves when the alert is closed
 */
export const showErrorAlert = async (
  body: string | undefined,
  title?: string | undefined,
  icon: boolean | undefined = true,
  isHtml: boolean | undefined = false
) => {
  const mode = localStorage.getItem("joy-mode");
  const customClass = {
    popup: "swal2-popup-dark ",
    title: "swal2-title-dark",
    confirmButton: "swal2-confirm-dark",
    cancelButton: "swal2-cancel-dark",
  };
  try {
    await Swal.fire({
      icon: icon ? "error" : undefined,
      title: title,
      confirmButtonColor: "#f27474",
      ...(isHtml ? { html: body } : { text: body }),
      customClass: mode === "dark" ? { ...customClass } : {},
    });
  } catch (err) {
    console.error("An error occurred while showing error alert:", err);
  }
};

/**
 * Displays a warning alert dialog with customizable content and styling.
 * 
 * @param {string | undefined} body - The main message content of the alert
 * @param {string | undefined} [title] - The title of the alert
 * @param {boolean | undefined} [icon=true] - Whether to show the warning icon
 * @param {boolean | undefined} [isHtml=false] - Whether the body content is HTML
 * 
 * @example
 * ```tsx
 * await showWarningAlert(
 *   "This action cannot be undone",
 *   "Warning",
 *   true,
 *   false
 * );
 * ```
 * 
 * @returns {Promise<void>} A promise that resolves when the alert is closed
 */
export const showWarningAlert = async (
  body: string | undefined,
  title?: string | undefined,
  icon: boolean | undefined = true,
  isHtml: boolean | undefined = false
) => {
  const mode = localStorage.getItem("joy-mode");
  const customClass = {
    popup: "swal2-popup-dark ",
    title: "swal2-title-dark",
    confirmButton: "swal2-confirm-dark",
    cancelButton: "swal2-cancel-dark",
  };
  try {
    await Swal.fire({
      icon: icon ? "warning" : undefined,
      title: title,
      confirmButtonColor: "#f8bb86",
      ...(isHtml ? { html: body } : { text: body }),
      customClass: mode === "dark" ? { ...customClass } : {},
    });
  } catch (err) {
    console.error("An error occurred while showing warning alert:", err);
  }
};

/**
 * Displays a confirmation alert dialog with customizable buttons and callback.
 * 
 * @param {string} title - The title of the confirmation dialog
 * @param {string} confirmButtonText - The text for the confirm button
 * @param {string} denyButtonText - The text for the deny button
 * @param {() => void} onConfirm - Callback function to execute when confirmed
 * 
 * @example
 * ```tsx
 * await showConfirmationAlert(
 *   "Delete Item?",
 *   "Yes, delete it",
 *   "No, keep it",
 *   () => handleDelete()
 * );
 * ```
 * 
 * @returns {Promise<void>} A promise that resolves when the alert is closed
 */
export const showConfirmationAlert = async (
  title: string,
  confirmButtonText: string,
  denyButtonText: string,
  onConfirm: () => void
) => {
  const mode = localStorage.getItem("joy-mode");
  const customClass = {
    popup: "swal2-popup-dark ",
    title: "swal2-title-dark",
    confirmButton: "swal2-confirm-dark",
    cancelButton: "swal2-cancel-dark",
  };
  try {
    const result = await Swal.fire({
      title: title,
      showDenyButton: true,
      confirmButtonText: confirmButtonText,
      denyButtonText: denyButtonText,
      icon: "warning",
      confirmButtonColor: "#3085d6",
      customClass: mode === "dark" ? { ...customClass } : {},
    });

    if (result.isConfirmed) {
      onConfirm();
    }
  } catch (err) {
    console.error("An error occurred while showing confirmation alert:", err);
  }
};

/**
 * Displays a 404 error alert dialog with predefined content and styling.
 * The alert is static and cannot be dismissed by clicking outside.
 * 
 * @example
 * ```tsx
 * showError404Alert();
 * ```
 * 
 * @returns {Promise<SweetAlertResult>} A promise that resolves with the alert result
 */
export const showError404Alert = () => {
  const mode = localStorage.getItem("joy-mode");
  const customClass = {
    popup: "swal2-popup-dark ",
    title: "swal2-title-dark",
    confirmButton: "swal2-confirm-dark",
    cancelButton: "swal2-cancel-dark",
  };
  return Swal.fire({
    icon: "error",
    title: "404 Nichtgefunden",
    text: "Hoppla... Die von Ihnen gesuchte Seite konnte nicht gefunden werden!",
    confirmButtonColor: "#f27474",
    backdrop: "static",
    allowOutsideClick: false,
    customClass: mode === "dark" ? { ...customClass } : {},
  });
};

export const showCommercialMessageAlert = async (
  body: string | undefined,
  title?: string | undefined,
) => {
  const mode = localStorage.getItem("joy-mode");
  const customClass = {
    popup: "swal2-popup-dark ",
    title: "swal2-title-dark",
    confirmButton: "swal2-confirm-dark",
    cancelButton: "swal2-cancel-dark",
  };
  try {
    await Swal.fire({
      icon: "info",
      title: title,
      confirmButtonColor: "#f8bb86",
      html: body,
      customClass: mode === "dark" ? { ...customClass } : {},
    });
  } catch (err) {
    console.error("An error occurred while showing warning alert:", err);
  }
};